import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import PlayIcon from './icons/videoControls/PlayIcon';
import PauseIcon from './icons/videoControls/PauseIcon';
import RestartIcon from './icons/videoControls/RestartIcon';
import useWindowSize from '../utils/useWindowSize';
import styles from './Video.module.css';

/** @param {string} str */
const generateHTMLIdFromString = str =>
  str.replace(/^[^a-zA-Z]+|[^a-zA-Z0-9-_]/g, '');

/** @param {string} url */
const generateVideoIdFromUrl = url => `video-${generateHTMLIdFromString(url)}`;

/**
 * TODO: complete CS-2816 for more performant video
 */

/**
 * @param {object} props
 * @param {boolean=} props.autoPlay
 * @param {boolean=} props.fullScreen
 * @param {string=} props.className
 * @param {string=} props.description
 * @param {string=} props.poster
 * @param {Array<{ width: number, urls: Array<string> }>=} props.srcset
 * @param {string=} props.url
 * @param {number=} props.height
 * @param {number=} props.width
 * @param {number=} props.containerMaxHeight
 */
export default function Video({
  autoPlay = false,
  fullScreen = false,
  className = '',
  containerMaxHeight,
  description,
  poster,
  srcset,
  url,
  height,
  width,
}) {
  const windowSize = useWindowSize();
  /**  @type {React.MutableRefObject<HTMLVideoElement|null>} */
  const videoRef = useRef(null);
  const [inViewRef, isInView] = useInView({
    fallbackInView: true,
    triggerOnce: true,
  });
  const setVideoRefs = useCallback(
    /** @param {HTMLVideoElement|null} node */
    node => {
      // Ref's from useRef needs to have the node assigned to `current`
      videoRef.current = node;
      // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
      inViewRef(node);
    },
    [inViewRef],
  );

  const [isVideoMetadataLoaded, setIsVideoMetadataLoaded] = useState(false);
  useEffect(() => {
    if (videoRef.current) {
      const videoMetadataLoadedHandler = () => setIsVideoMetadataLoaded(true);

      if (videoRef.current.readyState > 0) {
        videoMetadataLoadedHandler();
      } else {
        videoRef.current.addEventListener(
          'loadeddata',
          () => {
            videoMetadataLoadedHandler();
          },
          { once: true },
        );
      }

      return videoRef.current.removeEventListener(
        'loadeddata',
        videoMetadataLoadedHandler,
      );
    }
    return () => {};
  }, [videoRef]);

  const [playState, setPlayState] = useState(autoPlay ? 'PLAYING' : 'PAUSED');
  useEffect(() => {
    if (!videoRef.current) {
      return () => {};
    }

    const onPlayEventHandler = () => {
      setPlayState('PLAYING');
    };
    const onPauseEventHandler = () => {
      if (
        !autoPlay &&
        videoRef.current?.duration === videoRef.current?.currentTime
      ) {
        setPlayState('FINISHED');
      } else {
        setPlayState('PAUSED');
      }
    };

    videoRef.current.addEventListener('play', onPlayEventHandler);
    videoRef.current.addEventListener('pause', onPauseEventHandler);

    return () => {
      videoRef.current?.removeEventListener('play', onPlayEventHandler);
      videoRef.current?.removeEventListener('pause', onPauseEventHandler);
    };
  }, [videoRef]);

  function toggleVideoPlayState() {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }

  const hasAspectRatio = height && width;
  let initialVideoStyles = {};
  if (fullScreen) {
    if (hasAspectRatio) {
      initialVideoStyles = {
        aspectRatio: `${width}/${height}`,
      };
    } else {
      initialVideoStyles = {
        height: '100%',
        left: '0px',
        width: '100%',
        objectFit: 'cover',
        position: 'relative',
        top: '0px',
      };
    }
  }

  /** @type [React.CSSProperties, function] */
  const [videoElStyles, setVideoElStyles] = useState(initialVideoStyles);

  useEffect(() => {
    if (
      !videoRef?.current ||
      !isVideoMetadataLoaded ||
      !fullScreen ||
      hasAspectRatio
    ) {
      return;
    }

    const aspectRatio =
      videoRef.current.videoWidth / videoRef.current.videoHeight;

    const containerWidth = videoRef.current.parentElement?.offsetWidth;
    const containerHeight = videoRef.current.parentElement?.offsetHeight;

    if (
      Number.isNaN(aspectRatio) ||
      !aspectRatio ||
      !containerWidth ||
      !containerHeight
    ) {
      return;
    }

    const videoWidth = aspectRatio * containerHeight;
    const videoHeight = containerWidth / aspectRatio;

    if (videoWidth > containerWidth) {
      setVideoElStyles({
        height: '100%',
        left: `${(containerWidth - videoWidth) / 2}px`,
        width: `${videoWidth}px`,
        position: 'relative',
        top: '0px',
      });
    } else {
      setVideoElStyles({
        height: `${containerWidth / aspectRatio}px`,
        left: `0px`,
        width: `100%`,
        position: 'relative',
        top: `${(containerHeight - videoHeight) / 2}px`,
      });
    }
  }, [videoRef, isVideoMetadataLoaded]);

  const [source, setSource] = useState(
    (srcset || [])[0] || (url && { urls: [url] }),
  );
  if (!source) {
    throw new Error(
      'source is required for <Video>, passed in by props srcset or url',
    );
  }

  useEffect(() => {
    /** cant support srcset with how we style fullscreen videos without aspect ratio */
    if (fullScreen && !hasAspectRatio) {
      return;
    }

    const currentWidth = videoRef.current?.parentElement?.offsetWidth;
    if (!srcset || !currentWidth) {
      return;
    }
    let sizedSource = srcset.find(
      ({ width: sourceWidth }) => sourceWidth > currentWidth,
    );
    if (!sizedSource) {
      [sizedSource] = srcset.slice(-1);
    }

    // no need to download smaller video if larger one is already loaded
    if (sizedSource.width < source.width) {
      return;
    }

    setSource(sizedSource);
  }, [videoRef, windowSize]);

  useEffect(() => {
    if (isInView) {
      videoRef.current?.load();
    }
  }, [source, isInView]);

  let wrapperClassName;
  if (fullScreen) {
    if (hasAspectRatio) {
      wrapperClassName = styles.fullScreenWrapperWithAspectRatio;
    } else {
      wrapperClassName = styles.fullScreenWrapper;
    }
  } else {
    wrapperClassName = styles.embeddedSectionWrapper;
  }

  const videoId = generateVideoIdFromUrl(source.urls[0]);

  return (
    <div className={`${wrapperClassName} ${className}`}>
      {hasAspectRatio && containerMaxHeight && (
        <style>{`
          @media (width <= ${Math.floor((containerMaxHeight * width) / height)}px) {
            ${className ? `.${className.trim().split(' ').join('.')}` : ''} #${videoId} { max-height: 100%; }
          }
          @media (width > ${Math.floor((containerMaxHeight * width) / height)}px) {
            ${className ? `.${className.trim().split(' ').join('.')}` : ''} #${videoId} { max-width: 100%; }
          }
        `}</style>
      )}
      {/** TODO: investigate how marketing can provide captions */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        autoPlay={autoPlay}
        disablePictureInPicture
        disableRemotePlayback
        id={videoId}
        loop={autoPlay}
        muted={autoPlay}
        playsInline
        preload={isInView ? 'metadata' : 'none'}
        poster={poster}
        ref={setVideoRefs}
        height={height}
        width={width}
        style={videoElStyles}
      >
        {/** #t=0.001 is a neat little trick to get iOS to load the preview image */}
        {source.urls.map(urlStr => (
          <source
            key={urlStr}
            src={`${urlStr}#t=0.001`}
            type={`video/${urlStr.split('.').slice(-1)[0]}`}
          />
        ))}
      </video>
      <button
        className={styles.playPauseButton}
        onClick={toggleVideoPlayState}
        type="button"
      >
        {playState === 'PAUSED' && <PlayIcon className={styles.playPauseSvg} />}
        {playState === 'PLAYING' && (
          <PauseIcon className={styles.playPauseSvg} />
        )}
        {playState === 'FINISHED' && (
          <RestartIcon className={styles.restartSvg} />
        )}
      </button>
    </div>
  );
}
